import React, { useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { refreshAppData } from "../utils/refreshAppData";

type ClearCacheButtonProps = {
  /** Shown in the top notification strip (replaces a dismiss “×”). */
  variant?: "banner" | "inline";
  fullWidth?: boolean;
  className?: string;
};

export default function ClearCacheButton({
  variant = "inline",
  fullWidth = false,
  className = "",
}: ClearCacheButtonProps) {
  const queryClient = useQueryClient();
  const [clearing, setClearing] = useState(false);

  const handleClear = async () => {
    setClearing(true);
    try {
      await refreshAppData(queryClient);
    } catch {
      setClearing(false);
    }
  };

  const label = clearing ? "Refreshing…" : "Clear cache";

  const isBanner = variant === "banner";

  return (
    <button
      type="button"
      onClick={handleClear}
      disabled={clearing}
      title="Clear cached data and reload the page. You stay logged in."
      aria-label={label}
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
        isBanner
          ? "min-h-[2rem] border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-950 shadow-sm hover:bg-amber-100 sm:min-h-[2.25rem] sm:px-3.5 sm:text-sm"
          : "min-h-[2.5rem] border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800 hover:bg-violet-100"
      } ${fullWidth ? "w-full" : ""} ${className}`}
    >
      <ArrowPathIcon className={`h-4 w-4 shrink-0 ${clearing ? "animate-spin" : ""}`} />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}
